import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getStats, getFieldReports, getSessions } from '@/lib/api'
import { formatTimeAgo } from '@/lib/utils'
import {
  Building2,
  FileText,
  Users,
  HeartHandshake,
  AlertTriangle,
  Plus,
  ArrowRight,
  Clock,
} from 'lucide-react'

function getPriorityBadgeClass(priority: string) {
  switch (priority) {
    case 'critical':
      return 'sanctuary-badge sanctuary-badge-critical'
    case 'high':
      return 'sanctuary-badge sanctuary-badge-high'
    case 'medium':
      return 'sanctuary-badge sanctuary-badge-medium'
    default:
      return 'sanctuary-badge sanctuary-badge-low'
  }
}

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: getStats,
  })

  const { data: recentReports } = useQuery({
    queryKey: ['fieldReports', { limit: 5 }],
    queryFn: () => getFieldReports({ status: 'submitted' }),
  })

  const { data: activeSessions } = useQuery({
    queryKey: ['sessions', { status: 'in_progress' }],
    queryFn: () => getSessions({ status: 'in_progress' }),
  })

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const statCards = [
    {
      label: 'Soft Rooms',
      value: stats?.total_soft_rooms || 0,
      icon: Building2,
      iconBg: 'bg-blue-50 text-blue-600',
      href: '/soft-rooms',
    },
    {
      label: 'Active Sessions',
      value: stats?.active_sessions || 0,
      icon: Users,
      iconBg: 'bg-emerald-50 text-emerald-600',
      href: '/sessions',
    },
    {
      label: 'Pending Reports',
      value: stats?.pending_reports || 0,
      icon: AlertTriangle,
      iconBg: 'bg-amber-50 text-amber-600',
      href: '/field-reports',
      highlight: (stats?.pending_reports || 0) > 0,
    },
    {
      label: 'Referral Partners',
      value: stats?.total_partners || 0,
      icon: HeartHandshake,
      iconBg: 'bg-violet-50 text-violet-600',
      href: '/partners',
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Airport operations overview</p>
        </div>
        <div className="flex gap-2">
          <Link to="/field-reports">
            <Button size="sm" className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              New Report
            </Button>
          </Link>
          <Link to="/sessions">
            <Button size="sm" variant="outline" className="gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              New Session
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <Link key={card.label} to={card.href}>
            <div className="sanctuary-stat-card group">
              <div className="flex items-center justify-between">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${card.iconBg}`}>
                  <card.icon className="h-5 w-5" />
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <div className="mt-3">
                <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                <p className={`text-2xl font-bold tracking-tight ${card.highlight ? 'text-amber-600' : ''}`}>
                  {card.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Reports */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4.5 w-4.5 text-muted-foreground" />
              Pending Field Reports
            </CardTitle>
            <Link to="/field-reports" className="text-xs text-primary hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {recentReports && recentReports.length > 0 ? (
              <div className="space-y-2">
                {recentReports.slice(0, 5).map((report: any) => (
                  <div
                    key={report.id}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {report.airport_code} - {report.terminal || 'Unknown'}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        <span>{report.reporter_name}</span>
                        <span>·</span>
                        <Clock className="h-3 w-3" />
                        <span>{formatTimeAgo(report.created_at)}</span>
                      </div>
                    </div>
                    <span className={getPriorityBadgeClass(report.priority)}>
                      {report.priority}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No pending reports</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4.5 w-4.5 text-muted-foreground" />
              Active Sessions
            </CardTitle>
            <Link to="/sessions" className="text-xs text-primary hover:underline">
              View all
            </Link>
          </CardHeader>
          <CardContent>
            {activeSessions && activeSessions.length > 0 ? (
              <div className="space-y-2">
                {activeSessions.slice(0, 5).map((session: any) => (
                  <div
                    key={session.id}
                    className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">
                        {session.victim_first_name || 'Unknown'}{' '}
                        {session.victim_last_name || ''}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        <span>{session.responder_name}</span>
                        <span>·</span>
                        <Clock className="h-3 w-3" />
                        <span>Started {formatTimeAgo(session.started_at)}</span>
                      </div>
                    </div>
                    <span className="sanctuary-badge sanctuary-badge-active">
                      In Progress
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mb-2 opacity-40" />
                <p className="text-sm">No active sessions</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
